'use strict';
// Motor de Desempeño energético (ISO 50001) — API del paquete.
// Las fórmulas están en ./prototipo.js, extraído literal del prototipo de Sustemia (no se editan a mano).
// Acá solo se reproduce el mismo recorrido que hace el prototipo al importar y al dibujar una instalación,
// sin DOM: importSustemia() → upsertInstalacion() → renderAll().
const P = require('./prototipo');
const mapeo = require('./mapeo');
const importacion = require('./importacion');
const informe = require('./informe');
const { clonar, fijarContexto, inicializarDesdeArchivo } = require('./comun');

const SECCIONES_INFORME = ['0', '1', '2', '3', '4', '5', '6', '6bis', '7', '8', '9', '10', '10bis', '11', 'impl'];

// Archivo sustemia.energia (v1 o v2) ya guardado → instalación normalizada (forma interna del prototipo: serie en
// _monthly). Es el camino para calcular lo que está en la base. Para IMPORTAR un archivo nuevo (con lista de errores,
// combinación con la planta existente y vista previa) usar prepararImportacion().
// Lanza Error con el mensaje para el usuario si el archivo no es válido.
// opciones.permitirSinMeses: acepta una instalación todavía sin consumos (pantallas de una planta recién creada).
function importarArchivo(obj, opciones = {}) {
  fijarContexto(opciones);
  return P.normalizarInstalacion(P.san(inicializarDesdeArchivo(obj, opciones)));
}

// Para las pantallas: archivo v2 → instalación recalculada y resultados, sin lanzar.
// Devuelve { f, resultados } o { error } (mensaje para el usuario) si los datos guardados no pasan la validación.
function calcularVista(archivo, opciones = {}) {
  try {
    const f = recalcular(importarArchivo(archivo, { ...opciones, permitirSinMeses: true }), opciones);
    return { f, resultados: resultados(f, opciones) };
  } catch (e) {
    return { error: e.message };
  }
}

// Recalcula los derivados de una instalación normalizada (lo que hace renderAll() antes de dibujar).
// Muta f: _reg (LBEn), kWh/USD/repago de las medidas, _audit (28 requisitos).
function recalcular(f, opciones) {
  fijarContexto(opciones);
  P.limpiarReferenciasExternas(f);
  P.asegurarColecciones(f);
  f._reg = P.calcReg(f);
  P.recomputeEcms(f);
  f._audit = P.runAudit(f);
  if (!f._conclEdit) f._concl = null;
  return f;
}

const r4 = (v) => (v == null || !isFinite(v) ? null : Math.round(v * 1e4) / 1e4);

function serieDatos(c) {
  if (!c || c.pend || c.ok) return null;
  const d = c.data || {};
  return {
    tipo: c.type || 'mixto',
    etiquetas: (d.labels || []).map(String),
    series: (d.datasets || []).map((s) => ({
      nombre: String(s.label || ''),
      valores: (s.data || []).map((v) => (v == null ? null
        : Array.isArray(v) ? v.map(r4)
          : typeof v === 'object' ? { x: r4(v.x), y: r4(v.y) } : r4(v))),
    })),
  };
}

// Resultados de una instalación ya recalculada, con la forma de «esperado» en los fixtures (sustemia.energia.fixtures).
function resultados(f, opciones) {
  fijarContexto(opciones);
  P.ensureData(f);
  f._audit = P.runAudit(f);
  const L = (x) => P.LBL_VIS[x] || x;
  const sc = P.computeScore(f);
  const r = f._reg;
  const a0 = P.ahorroNormalizado(f);
  const a = a0 && !a0.faltaVar2 ? a0 : null;
  const d = P.dictamen(f);
  const ch = P.costoYHuella(f);
  const clausulas = {};
  Object.keys(f._audit).forEach((k) => { clausulas[k] = L(f._audit[k].label); });
  const graficos = {};
  const graficosDatos = {};
  P.GRAFICOS.forEach((g) => {
    const c = P.graficoBuild(f, g);
    graficos[g.id] = c.pend ? 'Pendiente: ' + c.pend : c.ok ? 'Estado: ' + c.ok : 'dibujado';
    const sd = serieDatos(c);
    if (sd) graficosDatos[g.id] = sd;
  });
  return {
    motor_version: P.MOTOR_VERSION,
    indice: sc.suficiente ? r4(sc.total) : 'Pendiente',
    indice_mostrado: P.scoreFmt(sc),
    faltan: sc.faltan || [],
    dimensiones: {
      eficiencia: r4(sc.eficiencia),
      tendencia: sc.tendencia == null ? null : r4(sc.tendencia),
      dato: r4(sc.dato),
      gestion: r4(sc.gestion),
    },
    lben: {
      tipo: r.tipo, etiqueta: P.lbEtiqueta(r), usable: r.usable,
      a: r4(r.a), b: r4(r.b), r2: r4(r.r2), cv: r4(r.cvrmse), p: r4(r.pA),
      n_base: r.nBase, n_reporte: r.nRep, n_excluidos: r.nExcl,
    },
    ahorro: a ? {
      kwh: r4(a.ahorro), pct: r4(a.pct), esperado: r4(a.esperado), real: r4(a.real),
      ic: [r4(a.ic[0]), r4(a.ic[1])], se: r4(a.se), t: r4(a.t), gl: a.gl, rho: r4(a.rho),
      significativo: a.significativo, fuera_de_rango: !!a.fueraRango,
    } : null,
    cobertura: P.coberturaPct(f),
    costo_anual: Number.isFinite(ch.costo) ? r4(ch.costo) : 'Pendiente',
    huella_tco2e: Number.isFinite(ch.co2) ? r4(ch.co2) : 'Pendiente',
    medidas: (f.ecms || []).map((e) => ({
      nombre: e.name, kwh: Number(e.kwh) || 0, usd: Number(e.usd) || 0,
      kwh_renovable: Number(e.kwhRenovable) || 0, payback: e.payback || '',
    })),
    implementacion_por_capitulo: P.computeClauseImplementation(f),
    estado_medicion: d.nivel,
    motivos_estado_medicion: d.motivos || [],
    conclusiones_seccion: Object.fromEntries(SECCIONES_INFORME.map((k) => [k, P.conclusionSeccion(f, k)])),
    clausulas,
    graficos,
    graficos_datos: graficosDatos,
  };
}

// Instalación normalizada → archivo sustemia.energia.v2 (exportSustemia() del prototipo, sin derivados).
function exportarArchivo(f) {
  return P.exportSustemia(f);
}

// Atajo: archivo sustemia.energia → resultados. Es la interfaz que pide verificar_fixtures.js.
function calcular(archivo, opciones = {}) {
  const f = importarArchivo(clonar(archivo), opciones);
  recalcular(f, opciones);
  return resultados(f, opciones);
}

module.exports = {
  MOTOR_VERSION: P.MOTOR_VERSION,
  importarArchivo,
  calcularVista,
  validarInstalacion: importacion.validarInstalacion,
  exportarArchivo,
  recalcular,
  resultados,
  calcular,
  // Documento de Mongo (inglés) ↔ archivo v2 (nombres de Sustemia).
  mongoAV2: mapeo.mongoAV2,
  v2AMongo: mapeo.v2AMongo,
  // Versiones de la LBEn (6.5): validación y armado de la versión nueva.
  lineaBase: require('./lineaBase'),
  // Importación JSON / CSV con lista de errores por sección y fila, reimportación y vista previa (hoja 19).
  prepararImportacion: importacion.prepararImportacion,
  idDeEntrada: importacion.idDeEntrada,
  // Campos de la instalación en Mongo (los que reemplaza una importación).
  CAMPOS_MONGO: mapeo.TABLA_INSTALACION.map((f) => f[0]),
  // Plantilla del modelo de relevamiento (.xlsx, Uint8Array): vacía o precargada con la instalación.
  plantillaXlsx: (f, vacia) => P.modeloRelevamiento(f || {}, !!vacia),
  // Informe A/B (con la conclusión de cada sección) y centro de descargas.
  informe: informe.informe,
  informeHtml: informe.informeHtml,
  guiaHtml: informe.guiaHtml,
  fijarImagenGrafico: informe.fijarImagenGrafico,
  archivoDescarga: informe.archivoDescarga,
  DESCARGAS: informe.DESCARGAS.map(({ clave, t, d, imprimir }) => ({ clave, t, d, imprimir: !!imprimir })),
  // Funciones y catálogos del prototipo, para pantallas, informe y descargas.
  prototipo: P,
  // Textos fijos de las pantallas (metodología, alcance del módulo…), extraídos y limpios.
  textos: require('./textos'),
};
